import type {
  ActivationPresentationCard,
  ActivationPresentationContent,
  ActivationPurposePresentation,
  PublicActivationDTO,
} from '@/types/activation/activation-pack';
import type { GeniePurpose } from '@/types/genie-purpose';
import { isValidGeniePurpose } from '@/types/genie-purpose';

const PURPOSE_CONTENT: Record<GeniePurpose, ActivationPurposePresentation> = {
  leads: {
    label: 'new enquiries',
    promiseVerb: 'start the right enquiry',
    fallbackDescription: 'Explain what you need and share the details required to begin an enquiry.',
    capabilities: [
      {
        genieIcon: 'target',
        title: 'Start an enquiry',
        body: 'Describe what you are looking for in your own words.',
      },
      {
        genieIcon: 'clipboard-check',
        title: 'Share useful details',
        body: 'Work through the information requested for the enquiry.',
      },
      {
        genieIcon: 'chat-check',
        title: 'See the next step',
        body: 'Continue with the options or guidance presented in the conversation.',
      },
    ],
    examplePrompts: [
      'I would like to make an enquiry.',
      'Can you help me find the right option?',
      'What details do you need from me?',
    ],
  },
  support: {
    label: 'support questions',
    promiseVerb: 'work through a support question',
    fallbackDescription: 'Describe the issue, ask a question, and follow the support guidance available to you.',
    capabilities: [
      {
        genieIcon: 'chat-check',
        title: 'Ask a support question',
        body: 'Start with the question you need help with.',
      },
      {
        genieIcon: 'files',
        title: 'Describe the issue',
        body: 'Add the details that make your situation clear.',
      },
      {
        genieIcon: 'target',
        title: 'Follow the guidance',
        body: 'Work through the response and the next steps made available.',
      },
    ],
    examplePrompts: [
      'Can you help me with a problem?',
      'What should I try next?',
      'Where can I find help with this?',
    ],
  },
  bookings: {
    label: 'booking requests',
    promiseVerb: 'take the next step toward a booking',
    fallbackDescription: 'Start a booking request and share the timing and service details that matter.',
    capabilities: [
      {
        genieIcon: 'calendar-check',
        title: 'Start a booking request',
        body: 'Say what you would like to book.',
      },
      {
        genieIcon: 'calendar-check',
        title: 'Share your preferences',
        body: 'Provide the timing and service details requested in the conversation.',
      },
      {
        genieIcon: 'chat-check',
        title: 'Continue with the next step',
        body: 'Follow the booking options or guidance that are presented.',
      },
    ],
    examplePrompts: [
      'I would like to make a booking.',
      'What booking options are available?',
      'What information do you need from me?',
    ],
  },
  orders: {
    label: 'order requests',
    promiseVerb: 'start an order request',
    fallbackDescription: 'Explain what you want to order and work through the details needed to continue.',
    capabilities: [
      {
        genieIcon: 'files',
        title: 'Start an order',
        body: 'Describe what you would like to order.',
      },
      {
        genieIcon: 'clipboard-check',
        title: 'Add the details',
        body: 'Provide the quantities, options, or other information requested.',
      },
      {
        genieIcon: 'chat-check',
        title: 'Review what comes next',
        body: 'Continue with the order guidance made available in the conversation.',
      },
    ],
    examplePrompts: [
      'I would like to place an order.',
      'Can you help me choose the right option?',
      'What details do you need for my order?',
    ],
  },
  quotes: {
    label: 'quote requests',
    promiseVerb: 'start a quote request',
    fallbackDescription: 'Share what you need and work through the inputs required for an estimate.',
    capabilities: [
      {
        genieIcon: 'document',
        title: 'Request a quote',
        body: 'Explain what you would like an estimate for.',
      },
      {
        genieIcon: 'clipboard-check',
        title: 'Provide the inputs',
        body: 'Work through the details needed to shape the request.',
      },
      {
        genieIcon: 'chat-check',
        title: 'Understand the next step',
        body: 'Continue with the quote guidance made available to you.',
      },
    ],
    examplePrompts: [
      'I would like to request a quote.',
      'What information do you need for an estimate?',
      'Can you help me choose the right option?',
    ],
  },
  surveys: {
    label: 'survey responses',
    promiseVerb: 'complete a survey conversation',
    fallbackDescription: 'Work through a structured set of questions and share your responses.',
    capabilities: [
      {
        genieIcon: 'clipboard-check',
        title: 'Begin the survey',
        body: 'Start when you are ready to share your response.',
      },
      {
        genieIcon: 'chat',
        title: 'Answer in your own words',
        body: 'Work through the questions one at a time.',
      },
      {
        genieIcon: 'chat-check',
        title: 'Complete your response',
        body: 'Follow the conversation through to the end of the survey.',
      },
    ],
    examplePrompts: [
      'I am ready to begin the survey.',
      'Can you repeat that question?',
      'What comes next?',
    ],
  },
  forms: {
    label: 'guided submissions',
    promiseVerb: 'complete a guided submission',
    fallbackDescription: 'Share the required information through a guided conversation.',
    capabilities: [
      {
        genieIcon: 'dashboard',
        title: 'Start the form',
        body: 'Begin the guided flow when you are ready.',
      },
      {
        genieIcon: 'clipboard-check',
        title: 'Provide each detail',
        body: 'Answer the requested fields one step at a time.',
      },
      {
        genieIcon: 'chat-check',
        title: 'Finish the submission',
        body: 'Follow the conversation through to the final step.',
      },
    ],
    examplePrompts: [
      'I am ready to fill this in.',
      'What information do I need?',
      'Can you guide me through the next step?',
    ],
  },
  interviews: {
    label: 'structured interviews',
    promiseVerb: 'take part in a structured interview',
    fallbackDescription: 'Work through a structured conversation and share your answers in your own words.',
    capabilities: [
      {
        genieIcon: 'microphone',
        title: 'Start the interview',
        body: 'Begin the conversation when you are ready.',
      },
      {
        genieIcon: 'chat',
        title: 'Respond naturally',
        body: 'Answer each question in your own words.',
      },
      {
        genieIcon: 'chat-check',
        title: 'Work through each stage',
        body: 'Follow the structured conversation to its final question.',
      },
    ],
    examplePrompts: [
      'I am ready to begin the interview.',
      'Could you repeat the question?',
      'What would you like to know next?',
    ],
  },
  reviews: {
    label: 'customer feedback',
    promiseVerb: 'share useful feedback',
    fallbackDescription: 'Describe your experience and work through the questions used to collect feedback.',
    capabilities: [
      {
        genieIcon: 'smile',
        title: 'Share your experience',
        body: 'Explain what went well or what could be better.',
      },
      {
        genieIcon: 'chat',
        title: 'Add useful detail',
        body: 'Respond to the feedback questions in your own words.',
      },
      {
        genieIcon: 'chat-check',
        title: 'Complete the review',
        body: 'Follow the conversation through to the final prompt.',
      },
    ],
    examplePrompts: [
      'I would like to share some feedback.',
      'Can I tell you about my experience?',
      'What would you like me to review?',
    ],
  },
  training: {
    label: 'guided learning',
    promiseVerb: 'work through learning material',
    fallbackDescription: 'Use a guided conversation to explore the learning material made available to you.',
    capabilities: [
      {
        genieIcon: 'brain',
        title: 'Start with a topic',
        body: 'Choose the part of the material you want to work through.',
      },
      {
        genieIcon: 'chat',
        title: 'Ask about the material',
        body: 'Use the conversation to explore the content provided.',
      },
      {
        genieIcon: 'chat-check',
        title: 'Keep learning',
        body: 'Follow the available prompts and guidance at your own pace.',
      },
    ],
    examplePrompts: [
      'Where should I begin?',
      'Can you guide me through this topic?',
      'What should I learn next?',
    ],
  },
  general: {
    label: 'guided conversations',
    promiseVerb: 'start the right conversation',
    fallbackDescription: 'Start a conversation, explain what you need, and follow the information made available to you.',
    capabilities: [
      {
        genieIcon: 'chat',
        title: 'Start in your own words',
        body: 'Open the conversation and explain what you need.',
      },
      {
        genieIcon: 'files',
        title: 'Explore the available information',
        body: 'Ask about the topics this Genie has been set up to cover.',
      },
      {
        genieIcon: 'chat-check',
        title: 'Follow the next step',
        body: 'Continue with the response or options presented to you.',
      },
    ],
    examplePrompts: [
      'What can you help me with?',
      'Where should I start?',
      'What information is available here?',
    ],
  },
};

const GENIE_STEPS: ActivationPresentationCard[] = [
  {
    genieIcon: 'qr-code',
    title: 'Open or scan',
    body: 'Use a button on this page or point your camera at the code.',
  },
  {
    genieIcon: 'chat',
    title: 'Start the conversation',
    body: 'Say or type what you need in your own words.',
  },
  {
    genieIcon: 'chat-check',
    title: 'Follow the response',
    body: 'Continue with the information or next step presented to you.',
  },
];

const PORTAL_CAPABILITIES: ActivationPresentationCard[] = [
  {
    genieIcon: 'globe',
    title: 'Start in one place',
    body: 'Use the portal as the front door to the help that has been made available.',
  },
  {
    genieIcon: 'dashboard',
    title: 'Choose what you need',
    body: 'Review the available options and select the one that fits.',
  },
  {
    genieIcon: 'target',
    title: 'Continue to the right place',
    body: 'Open the relevant Genie or destination to keep going.',
  },
];

const PORTAL_STEPS: ActivationPresentationCard[] = [
  {
    genieIcon: 'qr-code',
    title: 'Open the portal',
    body: 'Use the button on this page or scan the code.',
  },
  {
    genieIcon: 'dashboard',
    title: 'Choose a path',
    body: 'Review the options made available in the portal.',
  },
  {
    genieIcon: 'chat',
    title: 'Continue from there',
    body: 'Open the Genie or destination that matches what you need.',
  },
];

const cleanText = (value: string | null | undefined, maxLength: number): string | null => {
  if (typeof value !== 'string') return null;
  const normalized = value.replace(/\s+/g, ' ').trim();
  if (!normalized) return null;
  if (normalized.length <= maxLength) return normalized;

  const clipped = normalized.slice(0, maxLength - 3);
  const lastSpace = clipped.lastIndexOf(' ');
  return `${(lastSpace > maxLength * 0.7 ? clipped.slice(0, lastSpace) : clipped).trim()}...`;
};

const formatAudience = (value: string | null): string | null => {
  const audience = cleanText(value, 60)?.toLowerCase();
  if (!audience) return null;

  const known: Record<string, string> = {
    business: 'business customers',
    businesses: 'business customers',
    consumer: 'customers',
    consumers: 'customers',
    customer: 'customers',
    customers: 'customers',
    internal: 'team members',
    staff: 'team members',
    team: 'team members',
  };

  return known[audience] || audience.replace(/[_-]+/g, ' ');
};

const resolveHighlights = (
  data: PublicActivationDTO,
  purposeLabel: string,
): string[] => {
  const audience = formatAudience(data.audience);
  const channelCount = data.channels.length;

  return [
    data.targetType === 'portal' ? 'One place to start' : `Built for ${purposeLabel}`,
    audience ? `For ${audience}` : null,
    channelCount > 0
      ? `${channelCount} ${channelCount === 1 ? 'way' : 'ways'} to connect`
      : 'Open from this page',
  ].filter((item): item is string => Boolean(item));
};

/**
 * Resolves presentation copy only from public DTO fields and the purpose
 * registry below. It deliberately avoids universal claims such as 24/7
 * availability, instant answers, multilingual support, or hand-off behavior.
 */
export const resolveActivationPresentationContent = (
  data: PublicActivationDTO,
): ActivationPresentationContent => {
  const name = cleanText(data.name, 100) || (data.targetType === 'portal' ? 'this portal' : 'this Genie');
  const businessName = cleanText(data.businessName, 100);
  const description = cleanText(data.description, 360);
  const tagline = cleanText(data.tagline, 220);
  const welcomeMessage = cleanText(data.welcomeMessage, 240);

  if (data.targetType === 'portal') {
    return {
      heroEyebrow: businessName ? `A help portal from ${businessName}` : 'Your help portal',
      statusLabel: 'Ready to explore',
      promiseHeadline: `${name} brings the available help together in one place.`,
      promiseBody:
        description || tagline || `Open ${name} to see the help and conversation options available to you.`,
      highlights: resolveHighlights(data, 'guided conversations'),
      capabilityHeadline: `A simple route into the help available through ${name}.`,
      capabilities: PORTAL_CAPABILITIES,
      examplesEyebrow: 'Start here',
      examplesHeadline: 'Choose the route that matches what you need.',
      examplePrompts: [
        'I need help with a question.',
        'I want to find the right conversation.',
        'Show me the available ways to get started.',
      ],
      welcomeMessage,
      examplesFootnote: `Open ${name} to see the options that have been made available.`,
      stepsHeadline: 'Three steps. That is it.',
      steps: PORTAL_STEPS,
      channelsHeadline: `Open ${name} in whichever way suits you.`,
      primaryActionLabel: `Open ${name}`,
      finaleHeadline: `Keep ${name} close.`,
      finaleBody: 'Get the app, scan the code, or save the link for later.',
      appBody: `Keep a quick route to ${name} on your phone.`,
    };
  }

  const purpose = isValidGeniePurpose(data.purpose) ? data.purpose : 'general';
  const profile = PURPOSE_CONTENT[purpose];

  return {
    heroEyebrow: businessName ? `A Genie from ${businessName}` : 'Meet your Genie',
    statusLabel: 'Ready when you are',
    promiseHeadline: `${name} is here to help you ${profile.promiseVerb}.`,
    promiseBody: description || tagline || profile.fallbackDescription,
    highlights: resolveHighlights(data, profile.label),
    capabilityHeadline: `What ${name} can help you do.`,
    capabilities: profile.capabilities,
    examplesEyebrow: 'Conversation starters',
    examplesHeadline: `Start with a question for ${name}.`,
    examplePrompts: profile.examplePrompts,
    welcomeMessage,
    examplesFootnote: 'Choose one of these starters or use your own words.',
    stepsHeadline: 'Three steps. That is it.',
    steps: GENIE_STEPS,
    channelsHeadline: `Start a conversation with ${name} in whichever way suits you.`,
    primaryActionLabel: `Talk to ${name}`,
    finaleHeadline: `Keep ${name} close.`,
    finaleBody: 'Get the app, scan the code, or save the link for later.',
    appBody: `Keep a quick route to ${name} on your phone.`,
  };
};
